/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { inject } from 'mobx-react';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Chip from '@material-ui/core/Chip';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

import IconButton from '@material-ui/core/IconButton';

//----------------------------------------------------------------------//
// GLOBAL COMPONENTS                                                    //
//----------------------------------------------------------------------//

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
import type TypeAccountStore from 'stores/AccountStore.js';

import ActionsList from 'components/ActionsList/ActionsList';

import companyActionsListData from 'views/LocationDetails/views/CompanyDetailsPage/actionsListData';
import countryActionsListData from 'views/LocationDetails/views/CountryDetailsPage/actionsListData';
import locationActionsListData from 'views/LocationDetails/views/LocationDetailsPage/actionsListData';

import AssetsCount from '../AssetsCount/AssetsCount';

import { ElectroTooltip } from 'components/ExtendedUiElements/customized';
import { formatSiteAddress } from 'components/SiteAddress/SiteAddress';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from './DropdownList.module.scss';
import { formulateUri } from '../../../../tables/components/ReportView/state';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

export type TypeProps = {
  location: Object,
};

@translate()
@inject('actionDrawerStore', 'accountStore', 'routing')
export default class DropdownList extends React.PureComponent<*> {
  isExpanded: boolean = false;

  handleExpandLocationChange = (location: number) => (ev: SyntheticInputEvent<*>) => {
    ev.stopPropagation();
    this.isExpanded = Boolean(this.props.expandedLocation !== location);
    this.props.onExpandLocationChange(location, this.isExpanded);
    this.props.accountStore.locationDetails = null;
    if (this.props.columnName === 'site') {
      this.props.recomputeRows();
    }
  };

  handleActionDrawer = (currentAction: string) => {
    const config = this.props.config;
    const location = this.props.location;
    const accountStore = this.props.accountStore;

    config.companyId = location.id;

    // set the company as selected
    // currentAction === 'addNewSite' &&
    // (accountStore.locationSelections[0] = location);
    // (accountStore.selected = location);
    accountStore.updateLocations(location, 0);

    const actionDrawerStore: TypeActionDrawerStore = this.props.actionDrawerStore;
    actionDrawerStore.openActionDrawer(currentAction);
  };

  renderCollapse(location: Object): React.Element<*> {
    const { accountStore, columnName, t }: TypeAccountStore = this.props;
    const config = this.props.config;
    const currentPath: string = this.props.routing.location.pathname;
    let endPath: string = '';

    if (this.props.index === 0) {
      endPath = location.id;
    } else if (this.props.index === 1 && accountStore.locationSelections.length > 0) {
      endPath = accountStore.locationSelections[0].id + '/' + location.id;
    } else if (this.props.index > 1 && accountStore.locationSelections.length > 1) {
      endPath =
        accountStore.locationSelections[0].id +
        '/' +
        accountStore.locationSelections[1].id +
        '/' +
        location.id;
    }

    const linkToLocationDetails = {
      pathname: `${currentPath}/location/${endPath}`,
      state: { location },
    };

    const linkToLocationAssets = {
      pathname: `/assets/location/${location.id}`,
      state: { location },
    };

    const linkToCountryAssets = {
      pathname: `/assets/company/${config.companyId}/country/${location.id}`,
      state: { location },
    };

    const actionListByColumn = {
      company: companyActionsListData,
      country: countryActionsListData,
      site: locationActionsListData,
      building: locationActionsListData,
      room: locationActionsListData,
      workspace: locationActionsListData,
    }[columnName];

    return (
      <Collapse
        className={styles.dropdownCollapse}
        in={this.props.expandedLocation === location.id}
        timeout="auto"
        unmountOnExit={true}
      >
        <List className={styles.dropdownCollapseList} component="ul" disablePadding={true}>
          <ListItem>
            <Link
              to={linkToLocationDetails}
              onClick={() => {
                const idx = this.props.index;
                accountStore.locationColumn = idx > 1 ? idx - 1 : 0;
              }}
            >
              <svg>
                <use xlinkHref="#icon-view-location-details" />
              </svg>
              <span>{t('general:actions.view_location_details')}</span>
            </Link>
          </ListItem>
          {columnName !== 'company' && columnName !== 'country' && location.assets_count > 0 && (
            <ListItem>
              <Link
                to={formulateUri('/assets', {
                  page: 1,
                  filters: [
                    { key: 'location_id', values: [location.id] },
                    { key: 'asset_type', values: ['Rented Open', 'Owned'] },
                  ],
                })}
              >
                <svg>
                  <use xlinkHref="#icon-assets" />
                </svg>
                <span>{t('general:actions.view_location_assets')}</span>
              </Link>
            </ListItem>
          )}
          <ActionsList
            actions={actionListByColumn}
            data={{
              details: location,
              accountId: accountStore.accountId,
              locationId: location.id,
              titleEnd: columnName,
            }}
            locationColumn={this.props.index}
            accordionList={true}
          />
        </List>
      </Collapse>
    );
  }

  renderListItemHeader(location: Object): React.Element<*> {
    const { t, columnName } = this.props;

    const activeLocation: string = this.props.selected ? styles.activeLocation : '';

    const activeDropdown: string =
      this.props.expandedLocation === location.id ? styles.activeDropdown : '';
    const locationType = location.asset_request_location_type
      ? location.asset_request_location_type
      : location.rush_asset_request_location_type;

    const imageRender = () => {
      if (locationType && columnName === 'company') {
        const image =
          locationType === 'delivery'
            ? '/icons/deliveries-pickups.png'
            : '/icons/manufacturers.png';

        return <img className={styles.imageType} alt={'delivery icon'} src={image} />;
      }

      return null;
    };

    let itemTitle = location.name;
    if (columnName === 'site' && location.address) {
      const { singleLine } = formatSiteAddress(location.address, location.siteName);
      itemTitle = singleLine;
    }

    return (
      <ListItem
        className={`${styles.dropdownListItemHeader} ${activeDropdown} ${activeLocation}`}
        onClick={this.props.onClick}
      >
        <ListItemText
          className={styles.dropdownListItemHeaderText}
          primary={
            <React.Fragment>
              {imageRender()}
              <span>{location.name}</span>
            </React.Fragment>
          }
          title={itemTitle}
          secondary={
            <span className={styles.secondaryHeader}>
              {location && location.is_default && (
                <Chip
                  className={styles.chip}
                  component="span"
                  label="default"
                  size="small"
                  variant="outlined"
                  color="secondary"
                />
              )}
              {location && location.assets_count >= 0 && (
                <AssetsCount assetsCount={location.assets_count} {...this.props} />
              )}
            </span>
          }
        />
        <ElectroTooltip title={t('general:options')}>
          <IconButton
            className={styles.dropdownCollapseButton}
            onClick={this.handleExpandLocationChange(location.id)}
          >
            <svg>
              <use xlinkHref="#icon-more-options" />
            </svg>
          </IconButton>
        </ElectroTooltip>
      </ListItem>
    );
  }

  render(): React.Element<*> {
    const { location }: TypeProps = this.props;

    return (
      <React.Fragment>
        {this.renderListItemHeader(location)}
        {this.renderCollapse(location)}
      </React.Fragment>
    );
  }
}
